import CSV from "@/UI/CSV";
import Menu from "@/UI/Menu";
import PrintPDF from "@/components/CommonUi/PrintPdf";
import { Button, Popover } from "antd";
import React, { useEffect, useState } from "react";
import { BsDatabaseExclamation, BsThreeDotsVertical } from "react-icons/bs";
import PayrollTable from "./PayrollTable";
import CommonSearch from "@/components/CommonUi/CommonSearch";
import Filter from "@/components/CommonUi/Filter";
import Segmented from "@/UI/Segmented";
import { CiCreditCard2, CiViewTable } from "react-icons/ci";
import ColVisibilityDropdown from "@/components/Shared/ColVisibilityDropdown";
import Pagination from "@/UI/Pagination";

const PayrollTableComponent = ({
  list,
  total,
  loading,
  children,
  filters,
  columns,
  extraFilter,
  title,
  setPageConfig,
  isSearch,
  loadingUiSize = 10,
  nestedRowKey,
  card,
  defaultView = "list",
  onRowSelect,
  selectedRowKeys,
  lock = {},
  selectedRowKey,
  rowClicker,
  inactiveChecker,
  sideListItems = [],
}) => {
  const fetchData = (page, count) => {
    setPageConfig((prev) => ({ ...prev, page, count }));
  };

  const [view, setView] = useState(defaultView);
  const [columnsToShow, setColumnsToShow] = useState([]);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    setColumnsToShow(columns);
  }, [columns?.length]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const columnsToShowHandler = (val) => setColumnsToShow(val);
  const handleChange = (value) => setView(value);

  const options = [
    { value: "card", icon: <CiCreditCard2 size={21} />, title: "Card" },
    { value: "list", icon: <CiViewTable size={21} />, title: "List" },
  ];

  const isRowSelection = !!onRowSelect && !!selectedRowKeys;

  const exportMenu = (
    <Menu
      items={[
        {
          key: "1",
          label: <PrintPDF list={list} columns={columns} title={title} notButton />,
        },
        {
          key: "2",
          label: (
            <ColVisibilityDropdown
              options={columns}
              columns={columns}
              columnsToShowHandler={columnsToShowHandler}
            />
          ),
        },
      ]}
    />
  );

  return (
    <>
      {/* Mobile Filters & Search */}
      {isMobile && (
        <div className="w-full mb-3 px-3">
          {isSearch && (
            <div className="w-full mb-2">
              <CommonSearch setPageConfig={setPageConfig} />
            </div>
          )}
          <div className="flex justify-between items-center">
            <div className="flex-1">
              <Filter
                setPageConfig={setPageConfig}
                filters={filters}
                extraFilter={extraFilter}
                pageConfig={{ page: 1, count: 10 }}
                isRowSelection={isRowSelection}
              />
            </div>
            <Popover
              content={exportMenu}
              placement="bottomRight"
              arrow={false}
              trigger="click"
            >
              <Button
                type="default"
                icon={<BsThreeDotsVertical size={18} />}
                className="px-3 ml-2"
              />
            </Popover>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="mt-2">
        <div className="w-full px-3 dark:text-yellow-50 flex flex-wrap gap-2 items-center flex-col-reverse sm:flex-row justify-between mb-3">
          {/* Left Side */}
          <div className="flex flex-wrap lg:flex-nowrap gap-2 w-full md:w-auto">
            {!isMobile && (
              <>
                {isSearch && (
                  <div className="w-full sm:w-[250px]">
                    <CommonSearch setPageConfig={setPageConfig} />
                  </div>
                )}
                <div className="hideScrollBar overflow-x-auto overflow-y-hidden w-full flex items-center gap-2">
                  <Filter
                    setPageConfig={setPageConfig}
                    filters={filters}
                    extraFilter={extraFilter}
                    pageConfig={{ page: 1, count: 10 }}
                    isRowSelection={isRowSelection}
                  />
                  {!card ? (
                    <ColVisibilityDropdown
                      options={columns}
                      columns={columns}
                      columnsToShowHandler={columnsToShowHandler}
                    />
                  ) : (
                    <Segmented options={options} value={view} onChange={handleChange} />
                  )}
                </div>
              </>
            )}
          </div>

          {/* Right Side */}
          <div className="flex items-center gap-2">
            {!isMobile && (
              <Button color="white" className="gap-1 text-xs">
                <PrintPDF list={list} columns={columns} title={title} />
              </Button>
            )}
          </div>
        </div>

        {view === "list" && (
          <PayrollTable
            loading={loading}
            columns={columnsToShow}
            nestedRowKey={nestedRowKey}
            data={list?.map((item) => ({ ...item, key: item?.userId }))}
            scroll={list?.length > 10 ? { y: window.innerHeight - 350 } : {}}
            loadingUiSize={loadingUiSize}
            onRowSelect={onRowSelect}
            selectedRowKeys={selectedRowKeys}
            lock={lock}
            selectedRowKey={selectedRowKey}
            rowClicker={rowClicker}
            inactiveChecker={inactiveChecker}
          />
        )}

        {view === "card" && (
          <>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
              {list?.map((item) => card(item))}
            </div>
            {!list?.length && !loading && (
              <div className="flex flex-col justify-center items-center h-full py-10">
                <BsDatabaseExclamation className="text-slate-200" size={70} />
                <p className="text-slate-400 text-sm mt-3 text-center">
                  No data found for the selected criteria.
                  <br />
                  Please try adjusting your filters or search terms.
                </p>
              </div>
            )}
          </>
        )}
      </div>

      {/* Pagination & Children */}
      <div className="w-full px-3 mt-4 flex flex-col-reverse sm:flex-row gap-2 items-center justify-between">
        <div className="flex-1 flex gap-2">
          <Button
            onClick={() => fetchData(1, total)}
            disabled={loading}
            className="!rounded-full !min-w-[120px]"
          >
            Reset
          </Button>
          <Pagination
            total={total}
            pageConfig={{ page: 1, count: 10 }}
            setPageConfig={setPageConfig}
            disabled={loading}
          />
        </div>

        {children && <div className="shrink-0">{children}</div>}
      </div>
    </>
  );
};

export default PayrollTableComponent;